import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { StatusBadge } from '@/components/StatusBadge';
import { ImagePreview } from '@/components/ImagePreview';
import { Button } from '@/components/ui/button';
import { RowActionsMenu } from '@/components/RowActionsMenu';
import { CardNo } from '@/components/links/RecordLinks';
import { Eye, Edit, FilePlus, ArrowUpDown, ArrowUp, ArrowDown, History } from 'lucide-react';
import { ColumnFilter, MonthRangeFilter, type MonthRangeFilterProps } from '@/components/ColumnFilter';
import type { Member } from '@/types';
import { getLifeStatus, displayMemberNo } from '@/lib/utils';
import { formatAadhaar } from '@/lib/aadhaar';

/** Only the first column (ID) can be sorted. */
export type SortField = 'id';
export type SortDirection = 'asc' | 'desc';

export interface ColumnSort {
  field: SortField;
  direction: SortDirection;
}

export interface TableColumn {
  id: string;
  label: string;
  sortable: boolean;
  renderHeader: (sort: ColumnSort | null, onSort: (field: SortField) => void) => React.ReactNode;
  renderCell: (member: Member) => React.ReactNode;
  className?: string;
}

const HeaderLabel: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <span className={`text-xs font-semibold tracking-wide text-gray-800 ${className ?? ''}`}>{children}</span>
);

const SortButton: React.FC<{
  field: SortField;
  currentSort: ColumnSort | null;
  onSort: (field: SortField) => void;
  children: React.ReactNode;
  className?: string;
}> = ({ field, currentSort, onSort, children, className }) => {
  const isActive = currentSort?.field === field;
  const isAsc = currentSort?.direction === 'asc';

  return (
    <Button
      variant="ghost"
      size="sm"
      className={`-ml-2 h-8 gap-1 px-2 text-xs font-semibold tracking-wide hover:bg-gray-100 ${
        isActive ? 'text-gray-900' : 'text-gray-800 hover:text-gray-900'
      } ${className ?? ''}`}
      onClick={() => onSort(field)}
    >
      {children}
      {isActive ? (
        isAsc ? (
          <ArrowUp className="h-3.5 w-3.5 text-[#4369a0]" />
        ) : (
          <ArrowDown className="h-3.5 w-3.5 text-[#4369a0]" />
        )
      ) : (
        <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />
      )}
    </Button>
  );
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '--';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '--';
  const day = String(date.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const displayValue = (value: string | null | undefined): string => {
  return value && value.trim() !== '' ? value : '--';
};

/** Life Status header choice ('' = none). UI only for now: the rows aren't filtered. */
export interface LifeStatusFilter {
  value: string;
  onChange: (value: string) => void;
}

export const createColumns = (
  sort: ColumnSort | null,
  onSort: (field: SortField) => void,
  onView: (member: Member) => void,
  onEdit: (member: Member) => void,
  onAddPolicy: (member: Member) => void,
  onTransactions: (member: Member) => void,
  joinedFilter: MonthRangeFilterProps,
  lifeStatusFilter: LifeStatusFilter,
): TableColumn[] => [
  {
    id: 'id',
    label: 'ID',
    sortable: true,
    className: 'w-[80px]',
    renderHeader: () => (
      <SortButton field="id" currentSort={sort} onSort={onSort} className="whitespace-nowrap">
        ID
      </SortButton>
    ),
    renderCell: (member) => (
      <span className="flex w-[13px] justify-center whitespace-nowrap tabular-nums text-gray-500 text-sm font-medium">
        {displayMemberNo(member)}
      </span>
    ),
  },
  {
    id: 'cardNo',
    label: 'Card No',
    sortable: false,
    className: 'w-[90px]',
    renderHeader: () => <HeaderLabel>Card No</HeaderLabel>,
    renderCell: (member) => (
      <span className="text-[13px] text-gray-700">
        <CardNo value={member.cardNo} />
      </span>
    ),
  },
  {
    id: 'member',
    label: 'Member',
    sortable: false,
    className: 'w-[240px] max-w-[250px] pr-2',
    renderHeader: () => <HeaderLabel>Member</HeaderLabel>,
    renderCell: (member) => (
      <div className="flex min-w-0 items-center gap-2.5">
        <ImagePreview src={member.profileImage || member.photo} alt={member.name || 'Member photo'}>
          <Avatar className="h-9 w-9 shrink-0 border border-white shadow-sm ring-1 ring-gray-200/60">
            <AvatarImage src={member.profileImage || member.photo} />
            <AvatarFallback className="bg-[#4369a0]/10 text-xs font-semibold text-[#4369a0]">
              {member.name
                ? member.name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                : '--'}
            </AvatarFallback>
          </Avatar>
        </ImagePreview>
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => onView(member)}
            className="block w-full cursor-pointer truncate text-left text-sm font-medium text-gray-900 hover:text-[#4369a0] hover:underline"
          >
            {displayValue(member.name)}
          </button>
          <div className="w-full truncate text-xs text-gray-500">{displayValue(member.email)}</div>
        </div>
      </div>
    ),
  },
  {
    id: 'lifeStatus',
    label: 'Life Status',
    sortable: false,
    className: 'w-[110px]',
    renderHeader: () => (
      <ColumnFilter
        label="Life Status"
        options={[
          { value: 'alive', label: 'Alive' },
          { value: 'deceased', label: 'Deceased' },
        ]}
        value={lifeStatusFilter.value}
        onChange={lifeStatusFilter.onChange}
      />
    ),
    renderCell: (member) => {
      return <StatusBadge status={getLifeStatus(member)} />;
    },
  },
  {
    id: 'phone',
    label: 'Phone',
    sortable: false,
    className: 'w-[130px] pl-1 whitespace-nowrap',
    renderHeader: () => <HeaderLabel>Phone</HeaderLabel>,
    renderCell: (member) => <div className="text-sm text-gray-700">{displayValue(member.phone)}</div>,
  },
  {
    id: 'aadhaar',
    label: 'Aadhaar No',
    sortable: false,
    className: 'w-[140px] whitespace-nowrap',
    renderHeader: () => <HeaderLabel>Aadhaar No</HeaderLabel>,
    renderCell: (member) => (
      <div className="text-sm tabular-nums text-gray-700">
        {member.aadhaarNo ? formatAadhaar(member.aadhaarNo) : '--'}
      </div>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    sortable: false,
    className: 'w-[100px]',
    renderHeader: () => <HeaderLabel>Status</HeaderLabel>,
    renderCell: (member) => {
      return <StatusBadge status={member.status ?? 'active'} />;
    },
  },
  {
    id: 'joinedDate',
    label: 'Joined',
    sortable: false,
    className: 'min-w-[140px]',
    renderHeader: () => <MonthRangeFilter label="Joined" {...joinedFilter} />,
    renderCell: (member) => <div className="text-sm text-gray-600">{formatDate(member.joinedDate)}</div>,
  },
  {
    id: 'address',
    label: 'Address',
    sortable: false,
    className: 'min-w-[180px] max-w-[220px]',
    renderHeader: () => <HeaderLabel>Address</HeaderLabel>,
    renderCell: (member) => (
      <div className="truncate text-sm text-gray-600" title={member.address || '--'}>
        {displayValue(member.address)}
      </div>
    ),
  },
  {
    id: 'actions',
    label: 'Actions',
    sortable: false,
    className: 'w-[72px] text-center',
    renderHeader: () => <HeaderLabel className="block text-center">Actions</HeaderLabel>,
    renderCell: (member) => (
      <div className="flex justify-center">
        <RowActionsMenu
          label={`Actions for ${member.name}`}
          actions={[
            { label: 'View Member', icon: Eye, onSelect: () => onView(member) },
            {
              label: 'Edit Member',
              icon: Edit,
              onSelect: () => onEdit(member),
            },
            {
              label: 'Add Policy',
              icon: FilePlus,
              onSelect: () => onAddPolicy(member),
            },
            { label: 'Transaction History', icon: History, onSelect: () => onTransactions(member) },
          ]}
        />
      </div>
    ),
  },
];
